/**
 * Quasar-compatible theme module.
 * Applies default and white-label colors to Quasar (--q-*) and legacy Vuetify (--v-*-base) CSS variables.
 * Color map SoT: @callcorpacd/design-tokens.
 */
import {
    defaultTheme,
    mergeTheme,
    normalizeColorVariables,
    applyTheme as applyTokenTheme,
    resolveThemeColor as resolveTokenColor,
} from '@callcorpacd/design-tokens';
import msTeamsTheme from '@/plugins/themes/MSTeamsTheme';
import { setCssVar } from 'quasar';

const themes = {
    defaultTheme,
    msTeamsTheme,
    whiteLabel: {},
};

let currentThemeName = 'defaultTheme';
let activeTheme = { ...defaultTheme };
const themeListeners = new Set();

function notifyThemeListeners() {
    for (const listener of themeListeners) {
        try {
            listener(activeTheme, currentThemeName);
        }
        catch {
            // Host listeners must not break theme apply.
        }
    }
}

function applyThemeToDocument(theme) {
    applyTokenTheme(theme, document.documentElement, { setCssVar });
    notifyThemeListeners();
}

function resolveThemeColor(color) {
    return resolveTokenColor(color, activeTheme);
}

export default {
    getTheme(name) {
        if (name && themes[name])
            return themes[name];
        return activeTheme;
    },
    getActiveTheme() {
        return activeTheme;
    },
    getActiveThemeName() {
        return currentThemeName;
    },
    setTheme(name) {
        if (!themes[name])
            return;

        currentThemeName = name;
        activeTheme = mergeTheme(activeTheme, themes[name]);
        applyThemeToDocument(activeTheme);
    },
    importWhiteLabelSettings(settings, currentThemeNameOverride) {
        currentThemeName = currentThemeNameOverride || 'defaultTheme';

        try {
            const overrides = normalizeColorVariables(settings?.ColorVariables);
            const hasCustomColors = Object.keys(overrides).length > 0;

            if (!hasCustomColors)
                return;

            themes.whiteLabel = overrides;
            activeTheme = mergeTheme(defaultTheme, overrides);
            applyThemeToDocument(activeTheme);
        }
        catch (e) {
            console.warn('Theme: importWhiteLabelSettings parse error', e);
        }
    },
    resolveThemeColor,
    /**
     * @param {(theme: Record<string, string>, name: string) => void} listener
     * @returns {() => void}
     */
    subscribe(listener) {
        if (typeof listener !== 'function')
            return () => {};
        themeListeners.add(listener);
        return () => themeListeners.delete(listener);
    },
};
